import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, Image as ImageIcon, Loader2, RefreshCw } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

/**
 * WhatsApp attachments remain private: the API checks the manager's workspace
 * before returning the bytes. We never place provider or bucket URLs in the DOM.
 */
export default function WhatsAppAttachment({ message }) {
  const { token } = useAuth();
  const [attachment, setAttachment] = useState(message?.attachment || null);
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setAttachment(message?.attachment || null);
  }, [message?.attachment]);

  const endpoint = `${API_BASE}/communications/whatsapp-attachments/${encodeURIComponent(message?.id || '')}`;
  const ready = attachment?.status === 'ready';
  const isImage = ready && String(attachment?.mime_type || '').startsWith('image/');

  useEffect(() => {
    if (!token || !message?.id || !isImage) {
      setImageUrl('');
      return undefined;
    }
    let alive = true;
    let objectUrl = '';
    axios.get(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    }).then((response) => {
      if (!alive) return;
      objectUrl = URL.createObjectURL(response.data);
      setImageUrl(objectUrl);
      setError('');
    }).catch(() => {
      if (alive) setError('Preview unavailable. Try downloading the image.');
    });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint, isImage, message?.id, token]);

  const download = useCallback(async () => {
    if (!token || !message?.id) return;
    setBusy(true);
    setError('');
    try {
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = String(attachment?.filename || 'whatsapp-attachment')
        .replace(/[\\/:*?"<>|]/g, '_').slice(0, 120);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      setError('Could not download this attachment.');
    } finally {
      setBusy(false);
    }
  }, [attachment?.filename, endpoint, message?.id, token]);

  const retry = useCallback(async () => {
    if (!token || !message?.id) return;
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(`${endpoint}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAttachment(response.data?.attachment || { status: 'unavailable' });
    } catch (failure) {
      setError(failure?.response?.data?.detail || 'Could not retrieve this attachment.');
    } finally {
      setBusy(false);
    }
  }, [endpoint, message?.id, token]);

  if (!attachment || !message?.id) return null;
  const label = String(attachment.filename || (attachment.kind === 'image' ? 'Screenshot' : 'Document'));

  return (
    <div className="mt-3 max-w-sm" data-testid="whatsapp-inbound-attachment">
      {ready ? (
        <>
          {isImage ? (
            imageUrl ? (
              <a href={imageUrl} target="_blank" rel="noopener noreferrer" aria-label="Open screenshot">
                <img className="max-h-80 w-auto max-w-full rounded-lg border border-slate-200 object-contain" src={imageUrl} alt="WhatsApp screenshot" />
              </a>
            ) : (
              <div className="flex items-center gap-2 text-xs text-slate-500"><ImageIcon className="h-4 w-4" /> Screenshot preview loading</div>
            )
          ) : null}
          <button type="button" onClick={download} disabled={busy} className="mt-2 inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isImage ? <Download className="h-4 w-4" /> : <FileText className="h-4 w-4" />)}
            {isImage ? 'Save screenshot' : `Download ${label}`}
          </button>
        </>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">
          <p>{attachment.status === 'pending' ? 'Retrieving the attachment…' : `${label} could not be retrieved.`}</p>
          {attachment.status !== 'pending' && message?.provider_media_id ? (
            <button type="button" disabled={busy} onClick={retry} className="mt-2 inline-flex items-center gap-1 font-semibold underline disabled:opacity-60">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Retry download
            </button>
          ) : null}
        </div>
      )}
      {error ? <p role="alert" className="mt-2 text-xs text-red-700">{error}</p> : null}
    </div>
  );
}
